package org.teddapp.project.ui.finance

import org.teddapp.project.ui.TeddSize

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Finance_account
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddTokens

internal val ACCOUNT_TYPE_CHOICES = listOf(
    "checking" to "Compte courant",
    "savings" to "Épargne",
    "mobile" to "Mobile money",
    "card" to "Carte étrangère",
    "cash" to "Espèces",
)

internal val ACCOUNT_CURRENCIES = listOf("XOF", "USD", "EUR")

internal val ACCOUNT_ICON_CHOICES = listOf(
    "wallet", "spark", "flame", "home", "heart", "users",
    "book", "target", "category", "flag", "calendar", "settings",
)

internal val ACCOUNT_COLOR_CHOICES = listOf(
    "6366F1", "10B981", "22D3EE", "F59E0B", "EF4444", "A855F7", "EC4899", "64748B",
)

internal data class AccountDraft(
    val name: String,
    val type: String,
    val currency: String,
    val balance: Long,
    val icon: String,
    val color: String,
    val bank: String?,
    val last4: String?,
    val lowThreshold: Long?,
    val highThreshold: Long?,
)

@Composable
internal fun AccountEditPanel(
    existing: Finance_account?,
    onClose: () -> Unit,
    onSave: (AccountDraft) -> Unit,
    onDelete: (() -> Unit)?,
) {
    var name by remember(existing?.id) { mutableStateOf(existing?.name ?: "") }
    var type by remember(existing?.id) { mutableStateOf(existing?.type ?: "checking") }
    var currency by remember(existing?.id) { mutableStateOf(existing?.currency ?: "XOF") }
    var balance by remember(existing?.id) { mutableStateOf((existing?.balance ?: 0L).toString()) }
    var icon by remember(existing?.id) { mutableStateOf(existing?.icon ?: ACCOUNT_ICON_CHOICES.first()) }
    var color by remember(existing?.id) { mutableStateOf((existing?.color ?: "#${ACCOUNT_COLOR_CHOICES.first()}").trimStart('#')) }
    var bank by remember(existing?.id) { mutableStateOf(existing?.bank ?: "") }
    var last4 by remember(existing?.id) { mutableStateOf(existing?.last4 ?: "") }
    var lowThreshold by remember(existing?.id) { mutableStateOf(existing?.low_threshold?.toString() ?: "") }
    var highThreshold by remember(existing?.id) { mutableStateOf(existing?.high_threshold?.toString() ?: "") }

    val isEdit = existing != null
    val valid = name.isNotBlank() && balance.toLongOrNull() != null

    val fullScreen = org.teddapp.project.ui.LocalUseFullScreenForm.current
    Dialog(
        onDismissRequest = onClose,
        properties = DialogProperties(usePlatformDefaultWidth = false, dismissOnClickOutside = true),
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .then(
                    if (fullScreen) Modifier.background(TeddTokens.Bg)
                    else Modifier.background(Color.Black.copy(alpha = 0.35f))
                )
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onClose,
                ),
        ) {
            Column(
                modifier = Modifier
                    .then(
                        if (fullScreen) Modifier.fillMaxSize()
                        else Modifier.align(Alignment.CenterEnd).width(TeddSize.SidePanel).fillMaxHeight()
                    )
                    .background(TeddTokens.Bg)
                    .then(
                        if (fullScreen) Modifier
                        else Modifier.border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                    )
                    .then(if (fullScreen) Modifier.imePadding() else Modifier)
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = {},
                    ),
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                        .padding(horizontal = 22.dp, vertical = 16.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    // Preview avatar
                    Box(
                        modifier = Modifier
                            .size(40.dp)
                            .clip(RoundedCornerShape(TeddTokens.RSm))
                            .background(
                                Brush.linearGradient(listOf(parseFinHex("#$color"), parseFinHex("#$color").copy(alpha = 0.67f))),
                                RoundedCornerShape(TeddTokens.RSm),
                            ),
                        contentAlignment = Alignment.Center,
                    ) {
                        TeddIcon(name = icon, size = 18.dp, color = TeddTokens.OnAction, strokeWidth = 2.dp)
                    }
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = if (isEdit) "MODIFIER LE COMPTE" else "NOUVEAU COMPTE",
                            color = TeddTokens.InkMuted,
                            fontSize = 11.sp,
                            fontWeight = FontWeight.SemiBold,
                            letterSpacing = 0.6.sp,
                        )
                        Text(
                            text = name.ifBlank { "Sans nom" },
                            color = TeddTokens.Ink,
                            fontSize = 15.sp,
                            fontWeight = FontWeight.SemiBold,
                        )
                    }
                    Box(
                        modifier = Modifier
                            .clip(RoundedCornerShape(TeddTokens.RXs))
                            .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RXs))
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = onClose,
                            )
                            .padding(8.dp),
                    ) { TeddIcon(name = "plus", size = 14.dp, color = TeddTokens.InkMuted) }
                }

                Column(
                    modifier = Modifier
                        .weight(1f)
                        .verticalScroll(rememberScrollState())
                        .padding(22.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                ) {
                    Column {
                        FieldLabel("Nom du compte")
                        Spacer(modifier = Modifier.height(6.dp))
                        SmallTextField(value = name, onChange = { name = it }, placeholder = "Ex. Compte courant")
                    }
                    // Type segmented (2x2)
                    Column {
                        FieldLabel("Type")
                        Spacer(modifier = Modifier.height(6.dp))
                        val rows = ACCOUNT_TYPE_CHOICES.chunked(2)
                        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                            rows.forEach { rowChoices ->
                                Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                                    rowChoices.forEach { (k, label) ->
                                        val on = type == k
                                        Box(
                                            modifier = Modifier
                                                .weight(1f)
                                                .height(34.dp)
                                                .clip(RoundedCornerShape(TeddTokens.RSm))
                                                .background(
                                                    if (on) TeddTokens.LimeSoft else TeddTokens.Surface,
                                                    RoundedCornerShape(TeddTokens.RSm),
                                                )
                                                .border(
                                                    1.dp,
                                                    if (on) TeddTokens.LimeStrong.copy(alpha = 0.4f) else TeddTokens.Line,
                                                    RoundedCornerShape(TeddTokens.RSm),
                                                )
                                                .clickable(
                                                    interactionSource = remember { MutableInteractionSource() },
                                                    indication = null,
                                                    onClick = { type = k },
                                                ),
                                            contentAlignment = Alignment.Center,
                                        ) {
                                            Text(
                                                text = label,
                                                color = if (on) TeddTokens.LimeStrong else TeddTokens.Ink,
                                                fontSize = 12.5.sp,
                                                fontWeight = if (on) FontWeight.SemiBold else FontWeight.Medium,
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                    // Currency + initial balance
                    Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                        Column(modifier = Modifier.width(150.dp)) {
                            FieldLabel("Devise")
                            Spacer(modifier = Modifier.height(6.dp))
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clip(RoundedCornerShape(TeddTokens.RSm))
                                    .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                                    .padding(3.dp),
                            ) {
                                ACCOUNT_CURRENCIES.forEach { c ->
                                    val on = currency == c
                                    Box(
                                        modifier = Modifier
                                            .weight(1f)
                                            .height(28.dp)
                                            .clip(RoundedCornerShape(TeddTokens.RXs))
                                            .background(if (on) TeddTokens.Surface else Color.Transparent, RoundedCornerShape(TeddTokens.RXs))
                                            .clickable(
                                                interactionSource = remember { MutableInteractionSource() },
                                                indication = null,
                                                onClick = { currency = c },
                                            ),
                                        contentAlignment = Alignment.Center,
                                    ) {
                                        Text(
                                            text = c,
                                            color = if (on) TeddTokens.Ink else TeddTokens.InkMuted,
                                            fontSize = 11.sp,
                                            fontWeight = FontWeight.SemiBold,
                                        )
                                    }
                                }
                            }
                        }
                        Column(modifier = Modifier.weight(1f)) {
                            FieldLabel(if (isEdit) "Solde actuel" else "Solde initial")
                            Spacer(modifier = Modifier.height(6.dp))
                            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                Box(modifier = Modifier.weight(1f)) {
                                    SmallTextField(
                                        value = balance,
                                        onChange = { v ->
                                            if (v.all { c -> c.isDigit() || c == '-' }) balance = v
                                        },
                                        placeholder = "0",
                                        visualTransformation = ThousandsVisualTransformation(),
                                    )
                                }
                                Text(
                                    text = when (currency) {
                                        "USD" -> "$"
                                        "EUR" -> "€"
                                        else -> "F"
                                    },
                                    color = TeddTokens.InkMuted,
                                    fontSize = 13.sp,
                                    fontWeight = FontWeight.Medium,
                                )
                            }
                        }
                    }
                    // Icon + color
                    Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                        Column(modifier = Modifier.weight(1f)) {
                            FieldLabel("Icône")
                            Spacer(modifier = Modifier.height(8.dp))
                            ChoiceGrid(
                                items = ACCOUNT_ICON_CHOICES,
                                selected = icon,
                                onChange = { icon = it },
                                renderItem = { i, on ->
                                    TeddIcon(
                                        name = i,
                                        size = 16.dp,
                                        color = if (on) TeddTokens.OnAction else TeddTokens.InkMuted,
                                        strokeWidth = 1.8.dp,
                                    )
                                },
                                bgFor = { _, on -> if (on) parseFinHex("#$color") else TeddTokens.SurfaceSunken },
                            )
                        }
                        Column(modifier = Modifier.width(170.dp)) {
                            FieldLabel("Couleur")
                            Spacer(modifier = Modifier.height(8.dp))
                            ChoiceGrid(
                                items = ACCOUNT_COLOR_CHOICES,
                                selected = color,
                                onChange = { color = it },
                                renderItem = { _, _ -> },
                                bgFor = { c, _ -> parseFinHex("#$c") },
                                borderFor = { c, on -> if (on) TeddTokens.Ink else parseFinHex("#$c").copy(alpha = 0.5f) },
                            )
                        }
                    }
                    // Bank + last4
                    Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                        Column(modifier = Modifier.weight(1f)) {
                            FieldLabel("Banque (optionnel)")
                            Spacer(modifier = Modifier.height(6.dp))
                            SmallTextField(value = bank, onChange = { bank = it }, placeholder = "Ex. Ecobank")
                        }
                        Column(modifier = Modifier.width(140.dp)) {
                            FieldLabel("4 derniers")
                            Spacer(modifier = Modifier.height(6.dp))
                            SmallTextField(value = last4, onChange = { v ->
                                if (v.all { c -> c.isDigit() } && v.length <= 4) last4 = v
                            }, placeholder = "1234")
                        }
                    }
                    // Seuils — valeurs fixes, juste pour marquer l'atteinte (aucun blocage).
                    Column {
                        FieldLabel("Seuils (optionnel — juste un marqueur)")
                        Spacer(modifier = Modifier.height(6.dp))
                        Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                            Column(modifier = Modifier.weight(1f)) {
                                Text("Limite basse", color = TeddTokens.InkMuted, fontSize = 11.sp)
                                Spacer(modifier = Modifier.height(4.dp))
                                SmallTextField(
                                    value = lowThreshold,
                                    onChange = { v -> if (v.all { it.isDigit() }) lowThreshold = v },
                                    placeholder = "Ex. 10000",
                                    visualTransformation = ThousandsVisualTransformation(),
                                )
                            }
                            Column(modifier = Modifier.weight(1f)) {
                                Text("Seuil positif", color = TeddTokens.InkMuted, fontSize = 11.sp)
                                Spacer(modifier = Modifier.height(4.dp))
                                SmallTextField(
                                    value = highThreshold,
                                    onChange = { v -> if (v.all { it.isDigit() }) highThreshold = v },
                                    placeholder = "Ex. 500000",
                                    visualTransformation = ThousandsVisualTransformation(),
                                )
                            }
                        }
                    }
                }

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                        .padding(16.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    if (onDelete != null) {
                        TeddButton(
                            text = "Supprimer",
                            kind = TeddButtonKind.Danger,
                            size = TeddButtonSize.Md,
                            onClick = onDelete,
                            leadingIcon = { TeddIcon(name = "trash", size = 13.dp, color = TeddTokens.OnAction, strokeWidth = 2.dp) },
                        )
                    }
                    Spacer(modifier = Modifier.weight(1f))
                    TeddButton(text = "Annuler", kind = TeddButtonKind.Secondary, size = TeddButtonSize.Md, onClick = onClose)
                    TeddButton(
                        text = if (isEdit) "Enregistrer" else "Créer",
                        kind = TeddButtonKind.Primary,
                        size = TeddButtonSize.Md,
                        enabled = valid,
                        onClick = {
                            onSave(
                                AccountDraft(
                                    name = name.trim(),
                                    type = type,
                                    currency = currency,
                                    balance = balance.toLongOrNull() ?: 0L,
                                    icon = icon,
                                    color = "#$color",
                                    bank = bank.trim().ifBlank { null },
                                    last4 = last4.trim().ifBlank { null },
                                    lowThreshold = lowThreshold.trim().toLongOrNull(),
                                    highThreshold = highThreshold.trim().toLongOrNull(),
                                ),
                            )
                        },
                        leadingIcon = { TeddIcon(name = "check", size = 13.dp, color = TeddTokens.OnAction, strokeWidth = 2.2.dp) },
                    )
                }
            }
        }
    }
}

/**
 * Marqueurs de seuil atteint (point 4) — purement informatif. À poser sur le hero du
 * compte. [onLight] = true sur un fond clair (texte coloré), false sur le hero coloré
 * (pastille blanche translucide + texte blanc).
 */
@Composable
internal fun AccountThresholdBadges(
    account: Finance_account,
    onLight: Boolean = true,
    modifier: Modifier = Modifier,
) {
    val low = account.low_threshold
    val high = account.high_threshold
    val lowHit = low != null && account.balance <= low
    val highHit = high != null && account.balance >= high
    if (!lowHit && !highHit) return
    Row(modifier, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
        if (highHit) ThresholdBadge("Objectif atteint", "check", TeddTokens.Success, onLight)
        if (lowHit) ThresholdBadge("Limite atteinte", "flag", TeddTokens.Danger, onLight)
    }
}

@Composable
private fun ThresholdBadge(label: String, icon: String, accent: Color, onLight: Boolean) {
    val bg = if (onLight) accent.copy(alpha = 0.12f) else TeddTokens.OnAction.copy(alpha = 0.20f)
    val fg = if (onLight) accent else TeddTokens.OnAction
    Row(
        modifier = Modifier
            .clip(RoundedCornerShape(TeddTokens.RFull))
            .background(bg, RoundedCornerShape(TeddTokens.RFull))
            .padding(horizontal = 9.dp, vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        TeddIcon(name = icon, size = 11.dp, color = fg, strokeWidth = 2.dp)
        Text(label, color = fg, fontSize = 10.5.sp, fontWeight = FontWeight.SemiBold)
    }
}

@Composable
private fun FieldLabel(text: String) {
    Text(text = text, color = TeddTokens.InkMuted, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
}

@Composable
private fun SmallTextField(
    value: String,
    onChange: (String) -> Unit,
    placeholder: String,
    visualTransformation: androidx.compose.ui.text.input.VisualTransformation =
        androidx.compose.ui.text.input.VisualTransformation.None,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(38.dp)
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
            .padding(horizontal = 12.dp),
        contentAlignment = Alignment.CenterStart,
    ) {
        BasicTextField(
            value = value,
            onValueChange = onChange,
            singleLine = true,
            textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 13.sp),
            cursorBrush = SolidColor(TeddTokens.LimeStrong),
            visualTransformation = visualTransformation,
            modifier = Modifier.fillMaxWidth(),
            decorationBox = { inner ->
                if (value.isEmpty()) Text(placeholder, color = TeddTokens.InkMuted, fontSize = 13.sp)
                inner()
            },
        )
    }
}

@Composable
private fun <T> ChoiceGrid(
    items: List<T>,
    selected: T,
    onChange: (T) -> Unit,
    renderItem: @Composable (T, Boolean) -> Unit,
    bgFor: @Composable (T, Boolean) -> Color,
    borderFor: @Composable (T, Boolean) -> Color = { _, on -> if (on) TeddTokens.LimeStrong else TeddTokens.Line },
) {
    val cols = 6
    val rows = items.chunked(cols)
    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        rows.forEach { row ->
            Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                row.forEach { it ->
                    val on = it == selected
                    Box(
                        modifier = Modifier
                            .size(32.dp)
                            .clip(RoundedCornerShape(TeddTokens.RSm))
                            .background(bgFor(it, on), RoundedCornerShape(TeddTokens.RSm))
                            .border(if (on) 2.dp else 1.dp, borderFor(it, on), RoundedCornerShape(TeddTokens.RSm))
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = { onChange(it) },
                            ),
                        contentAlignment = Alignment.Center,
                    ) {
                        renderItem(it, on)
                    }
                }
            }
        }
    }
}
